import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, Avatar, Button, CircularProgress, Skeleton, Stack } from '@mui/material';
import fileService from 'api/fileService';
import vendorService from 'api/vendorService';
import { gridSpacing } from 'store/constant';
import { toast } from 'react-toastify';

const VendorBanner = ({ data, isLoading, error }) => {
    const [logo, setLogo] = useState('');
    const [banner, setBanner] = useState('');
    const [uploading, setUploading] = useState(false);

    // Initialize from vendor data
    useEffect(() => {
        if (data?.vendor) {
            setLogo(data.vendor.logo || '');
            setBanner(data.vendor.banner || '');
        }
    }, [data]);

    const handleImageChange = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            const uploaded = await fileService.uploadFile(file);
            if (type === 'logo') setLogo(uploaded.url || uploaded);
            else setBanner(uploaded.url || uploaded);
        } catch (err) {
            toast.error('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setUploading(true);
            const updateData = { logo, banner };
            await vendorService.updateVendor(updateData);
            toast.success('Branding updated successfully!');
        } catch (err) {
            toast.error('Failed to update banner');
        } finally {
            setUploading(false);
        }
    };

    // --- Loading Skeleton ---
    if (isLoading) {
        return (
            <Grid container spacing={gridSpacing} paddingX={5} pt={3}>
                <Grid item md={5}>
                    <Stack spacing={1}>
                        <Skeleton variant="text" width={180} height={30} />
                        <Skeleton variant="text" width={220} height={20} />
                    </Stack>
                </Grid>

                <Grid item md={2}></Grid>

                <Grid item md={5}>
                    <Stack spacing={2}>
                        <Skeleton variant="rectangular" width={100} height={100} />
                        <Skeleton variant="rectangular" width="100%" height={120} />
                        <Skeleton variant="rounded" width={120} height={40} />
                    </Stack>
                </Grid>
            </Grid>
        );
    }

    if (error)
        return (
            <Typography p={4} color="error">
                Error loading vendor data ❌
            </Typography>
        );

    // --- Main UI ---
    return (
        <Grid container spacing={gridSpacing} paddingX={5} pt={3}>
            <Grid item md={5}>
                <Typography fontWeight="bold" fontSize={20}>
                    Profile & Banner
                </Typography>
                <Typography color="grey" fontSize={14}>
                    Update your vendor logo and banner
                </Typography>
            </Grid>

            <Grid item md={2}></Grid>

            <Grid item md={5}>
                {/* Logo */}
                <Typography fontWeight="bold" fontSize={16} gutterBottom>
                    Logo
                </Typography>
                <Avatar src={logo} sx={{ width: 100, height: 100, mb: 2, borderRadius: 2 }} variant="rounded" />
                <Button variant="outlined" component="label" sx={{ mb: 3 }} disabled={uploading}>
                    Change Logo
                    <input type="file" hidden onChange={(e) => handleImageChange(e, 'logo')} />
                </Button>

                {/* Banner */}
                <Typography fontWeight="bold" fontSize={16} gutterBottom>
                    Banner
                </Typography>
                <Avatar src={banner} sx={{ width: '100%', height: 120, mb: 2, borderRadius: 2 }} variant="rounded" />
                <Button variant="outlined" component="label" sx={{ mb: 3 }} disabled={uploading}>
                    Change Banner
                    <input type="file" hidden onChange={(e) => handleImageChange(e, 'banner')} />
                </Button>

                {/* Save */}
                <div>
                    <Button variant="contained" onClick={handleSubmit} disabled={uploading} sx={{ borderRadius: 2, px: 3, py: 1 }}>
                        {uploading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                    </Button>
                </div>
            </Grid>
        </Grid>
    );
};

VendorBanner.propTypes = {
    data: PropTypes.shape({
        vendor: PropTypes.shape({
            logo: PropTypes.string,
            banner: PropTypes.string
        })
    }),
    isLoading: PropTypes.bool,
    error: PropTypes.any
};

export default VendorBanner;
